import service from "@/api/service";
import {
  BASE_API_URL,
  MEMBER_API,
  ACCOUNT_API,
  POST_API,
  CONFERENCE_API,
  PAPER_API,
  AUTH_API,
  FINAL_REVIEW_API,
  REVIEW_API,
  REVIEWER_API,
  MAILTEMPLATE_API,
  CONSENT_API,
  HOMEPAGE_CONTENT_API,
  SIGNATURE_API,
  AUTHOR_API,
  FILE_API,
  ARCHIVE_API,
  GALLERY_API,
  JOURNAL_API,
  CONSENT_DOCUMENT_API,
  CONSENT_DOCUMENT_TYPE_API,
  JOURNAL_TYPE_API,
  JOURNAL_CONTENT_API,
  JOURNAL_CONTENT_GROUP_API,
  PAPER_COMMENT_API,
} from "@/api/config";

/**
 * 회원 관련 API 요청
 */
export const memberApi = {
  async getMembers(params) {
    const endpoint = `${BASE_API_URL}${MEMBER_API.members}`;
    const config = {
      params: params,
    };
    return await service.get(endpoint, config);
  },

  async getNonReviewerMembers() {
    const endpoint = `${BASE_API_URL}${MEMBER_API.nonReviewer}`;
    return await service.get(endpoint);
  },

  async getMember(member) {
    const endpoint = `${BASE_API_URL}${MEMBER_API.member(member)}`;
    return await service.get(endpoint);
  },

  async getTemporaryMember(params) {
    const endpoint = `${BASE_API_URL}${MEMBER_API.temporary}`;
    const config = {
      params: params,
    };
    return await service.get(endpoint, config);
  },

  async getAdmins(params) {
    const endpoint = `${BASE_API_URL}${MEMBER_API.admin}`;
    const config = {
      params: params,
    };
    return await service.get(endpoint, config);
  },

  async addAdminRole(data, params) {
    const endpoint = `${BASE_API_URL}${MEMBER_API.admin}`;
    const config = {
      params: params,
    };
    return await service.post(endpoint, data, config);
  },

  async deleteAdminRole(params) {
    const endpoint = `${BASE_API_URL}${MEMBER_API.admin}`;
    const config = {
      params: params,
    };
    return await service.delete(endpoint, config);
  },

  async updateMember(member) {
    const endpoint = `${BASE_API_URL}${MEMBER_API.member(member)}`;
    return await service.put(endpoint, member);
  },

  async updateMemberType(member, data) {
    const endpoint = `${BASE_API_URL}${MEMBER_API.memberType(member)}`;
    return await service.patch(endpoint, data);
  },

  async deleteMember(member) {
    const endpoint = `${BASE_API_URL}${MEMBER_API.deleteMember(member)}`;
    return await service.delete(endpoint);
  },

  async realDeleteMember(member) {
    const endpoint = `${BASE_API_URL}${MEMBER_API.realDeleteMember(member)}`;
    return await service.delete(endpoint);
  },
};

/**
 * 회계 관련 API 요청
 */
export const accountApi = {
  async getPayments(params) {
    const endpoint = `${BASE_API_URL}${ACCOUNT_API.payments}`;
    const config = {
      params: params,
    };
    return await service.get(endpoint, config);
  },

  async getPaymentsByMember(params) {
    const endpoint = `${BASE_API_URL}${ACCOUNT_API.paymentsByMember}`;
    const config = {
      params: params,
    };
    return await service.get(endpoint, config);
  },

  async getFeeTypes() {
    const endpoint = `${BASE_API_URL}${ACCOUNT_API.feeTypes}`;
    return await service.get(endpoint);
  },

  async updateFeeType(feeType) {
    const endpoint = `${BASE_API_URL}${ACCOUNT_API.updateFeeType(feeType)}`;
    return await service.put(endpoint, feeType);
  },

  async deleteFeeType(feeType) {
    const endpoint = `${BASE_API_URL}${ACCOUNT_API.updateFeeType(feeType)}`;
    return await service.delete(endpoint);
  },

  async createFeeType(data) {
    const endpoint = `${BASE_API_URL}${ACCOUNT_API.feeTypes}`;
    return await service.post(endpoint, data);
  },

  async createTosspaymentsOrder(data) {
    const endpoint = `${BASE_API_URL}${ACCOUNT_API.orderTosspayments}`;
    return await service.post(endpoint, data);
  },

  async confirmTosspayments(data) {
    const endpoint = `${BASE_API_URL}${ACCOUNT_API.confirmTosspayments}`;
    return await service.post(endpoint, data);
  },

  async requestCreatePaypalOrder(data) {
    const config = {
      headers: {
        "Content-Type": "application/json",
      },
    };
    const endpoint = `${BASE_API_URL}${ACCOUNT_API.createPaypalOrder}`;
    return await service.post(endpoint, data, config);
  },

  async requestCapturePaypalPayment(params) {
    const config = {
      params: params,
    };
    const endpoint = `${BASE_API_URL}${ACCOUNT_API.capturePaypalPayment}`;
    return await service.post(endpoint, null, config);
  },
};

/**
 * 게시글 관련 API 요청
 */
export const boardApi = {
  async getPosts(params) {
    const endpoint = `${BASE_API_URL}${POST_API.posts}`;
    const config = {
      params: params,
    };
    return await service.get(endpoint, config);
  },

  async getPost(data) {
    const endpoint = `${BASE_API_URL}${POST_API.showPost(data)}`;
    return await service.get(endpoint);
  },

  async createPost(data) {
    const endpoint = `${BASE_API_URL}${POST_API.createPost}`;
    return await service.post(endpoint, data);
  },

  async updatePost(data) {
    const endpoint = `${BASE_API_URL}${POST_API.showPost(data)}`;
    return await service.put(endpoint, data);
  },

  async deletePost(data) {
    const endpoint = `${BASE_API_URL}${POST_API.deletePost(data)}`;
    return await service.delete(endpoint);
  },
};

/**
 * 학술대회 관련 API 요청
 */
export const conferenceApi = {
  async getConferences(params) {
    const endpoint = `${BASE_API_URL}${CONFERENCE_API.conferences}`;
    const config = {
      params: params,
    };
    return await service.get(endpoint, config);
  },
  async getConferencesForSubmission() {
    const endpoint = `${BASE_API_URL}${CONFERENCE_API.conferenceForSubmission}`;
    return await service.get(endpoint);
  },

  async getConference(data) {
    const endpoint = `${BASE_API_URL}${CONFERENCE_API.showConference(data)}`;
    return await service.get(endpoint);
  },

  async createConference(data) {
    const endpoint = `${BASE_API_URL}${CONFERENCE_API.createConference}`;
    return await service.post(endpoint, data);
  },

  async updateConference(data) {
    const endpoint = `${BASE_API_URL}${CONFERENCE_API.updateConference(data)}`;
    return await service.put(endpoint, data);
  },

  async deleteConference(data) {
    const endpoint = `${BASE_API_URL}${CONFERENCE_API.deleteConference(data)}`;
    return await service.delete(endpoint);
  },
};

/**
 * 논문 관련 API 요청
 */
export const paperApi = {
  async getPapersByMember(params) {
    const endpoint = `${BASE_API_URL}${PAPER_API.papers}`;
    const config = {
      params: params,
    };
    return await service.get(endpoint, config);
  },

  async createPaper(data) {
    const endpoint = `${BASE_API_URL}${PAPER_API.papers}`;
    return await service.post(endpoint, data);
  },

  async getPapers(params) {
    const endpoint = `${BASE_API_URL}${PAPER_API.showPapers}`;
    const config = {
      params: params,
    };
    return await service.get(endpoint, config);
  },

  async getPendingReviewPapers(params) {
    const endpoint = `${BASE_API_URL}${PAPER_API.pendingReview}`;
    const config = {
      params: params,
    };
    return await service.get(endpoint, config);
  },

  async getPaper(data) {
    const endpoint = `${BASE_API_URL}${PAPER_API.showPaper(data)}`;
    return await service.get(endpoint);
  },

  async updatePaper(paper) {
    const endpoint = `${BASE_API_URL}${PAPER_API.updatePaper(paper)}`;
    return await service.put(endpoint, paper);
  },

  async updatePaperStatus(idx, status) {
    const endpoint = `${BASE_API_URL}${PAPER_API.updatePaperStatus({
      idx: idx,
    })}`;
    return await service.patch(endpoint, status);
  },

  async deletePaper(paper) {
    const endpoint = `${BASE_API_URL}${PAPER_API.deletePaper(paper)}`;
    return await service.delete(endpoint);
  },
};

/**
 * 인증 관련 API 요청
 */
export const authApi = {
  async socialLogin(params) {
    const endpoint = `${BASE_API_URL}${AUTH_API.socialLogin}`;
    const config = {
      params: params,
    };

    return await service.get(endpoint, config);
  },
  async socialSignup(params) {
    const endpoint = `${BASE_API_URL}${AUTH_API.socialSignup}`;
    const config = {
      params: params,
    };

    return await service.get(endpoint, config);
  },
  async login(data) {
    const endpoint = `${BASE_API_URL}${AUTH_API.login}`;

    return await service.post(endpoint, data);
  },
  async signup(data) {
    const endpoint = `${BASE_API_URL}${AUTH_API.signup}`;

    return await service.post(endpoint, data);
  },
  async getTemporaryPassword(data) {
    const endpoint = `${BASE_API_URL}${AUTH_API.temporaryPassword}`;

    return await service.patch(endpoint, data);
  },
  async changePassword(data) {
    const endpoint = `${BASE_API_URL}${AUTH_API.changePassword}`;

    return await service.patch(endpoint, data);
  },
  async refresh(headers) {
    const endpoint = `${BASE_API_URL}${AUTH_API.refreshToken}`;
    const config = {
      headers: headers,
    };

    return await service.get(endpoint, config);
  },
};

/**
 * 총평 관련 API 요청
 */
export const finalReviewApi = {
  async getFinalReviews(params) {
    const endpoint = `${BASE_API_URL}${FINAL_REVIEW_API.finalReview}`;
    const config = {
      params: params,
    };

    return await service.get(endpoint, config);
  },
  async createFinalReview(data) {
    const endpoint = `${BASE_API_URL}${FINAL_REVIEW_API.finalReview}`;
    return await service.post(endpoint, data);
  },

  async updateFinalReview(data) {
    const endpoint = `${BASE_API_URL}${FINAL_REVIEW_API.updateFinalReview(
      data
    )}`;
    return await service.put(endpoint, data);
  },
  async deleteFinalReview(finalReview) {
    const endpoint = `${BASE_API_URL}${FINAL_REVIEW_API.deleteFinalReview(
      finalReview
    )}`;
    return await service.delete(endpoint);
  },
  async showFinalReview(finalReview) {
    const endpoint = `${BASE_API_URL}${FINAL_REVIEW_API.showFinalReview(
      finalReview
    )}`;
    return await service.get(endpoint);
  },
  async showFinalReviewByPaper(params) {
    const endpoint = `${BASE_API_URL}${FINAL_REVIEW_API.finalReviewByPaper}`;
    const config = {
      params: params,
    };

    return await service.get(endpoint, config);
  },
};

/**
 * 심사 관련 API 요청
 */
export const reviewApi = {
  async getReviews(params) {
    const endpoint = `${BASE_API_URL}${REVIEW_API.review}`;
    const config = {
      params: params,
    };

    return await service.get(endpoint, config);
  },
  async getReviewsByPaper(params) {
    const endpoint = `${BASE_API_URL}${REVIEW_API.showReviewsByPaper}`;
    const config = {
      params: params,
    };

    return await service.get(endpoint, config);
  },
  async createReview(data) {
    const endpoint = `${BASE_API_URL}${REVIEW_API.createReview}`;
    return await service.post(endpoint, data);
  },
  async createReviewForResubmission(data) {
    const endpoint = `${BASE_API_URL}${REVIEW_API.createReviewForResubmission}`;
    return await service.post(endpoint, data);
  },
  async updateReview(review) {
    const endpoint = `${BASE_API_URL}${REVIEW_API.updateReview(review)}`;
    return await service.put(endpoint, review);
  },
  async deleteReview(review) {
    const endpoint = `${BASE_API_URL}${REVIEW_API.deleteReview(review)}`;
    return await service.delete(endpoint);
  },
  async showReview(review) {
    const endpoint = `${BASE_API_URL}${REVIEW_API.showReview(review)}`;
    return await service.get(endpoint);
  },
  
};

/**
 * 심사 위원 관련 API 요청
 */
export const reviewerApi = {
  async getReviewers(params) {
    const endpoint = `${BASE_API_URL}${REVIEWER_API.reviewer}`;
    const config = {
      params: params,
    };

    return await service.get(endpoint, config);
  },
  async getNonPaginatedReviewers() {
    const endpoint = `${BASE_API_URL}${REVIEWER_API.nonPagination}`;

    return await service.get(endpoint);
  },
  async createReviewer(data) {
    const endpoint = `${BASE_API_URL}${REVIEWER_API.reviewer}`;
    return await service.post(endpoint, data);
  },
  async updateReviewer(reviewer) {
    const endpoint = `${BASE_API_URL}${REVIEWER_API.updateReviewer(reviewer)}`;
    return await service.put(endpoint, reviewer);
  },
  async deleteReviewer(reviewer) {
    const endpoint = `${BASE_API_URL}${REVIEWER_API.deleteReviewer(reviewer)}`;
    return await service.delete(endpoint);
  },
  async showReviewer(reviewer) {
    const endpoint = `${BASE_API_URL}${REVIEWER_API.showReviewer(reviewer)}`;
    return await service.get(endpoint);
  },
};

/**
 * 심사 위원 관련 API 요청
 */
export const mailtemplateApi = {
  async getMailtemplates(params) {
    const endpoint = `${BASE_API_URL}${MAILTEMPLATE_API.mailtemplate}`;
    const config = {
      params: params,
    };

    return await service.get(endpoint, config);
  },
  async createMailtemplate(data) {
    const endpoint = `${BASE_API_URL}${MAILTEMPLATE_API.mailtemplate}`;
    return await service.post(endpoint, data);
  },
  async updateMailtemplate(data) {
    const endpoint = `${BASE_API_URL}${MAILTEMPLATE_API.updateMailtemplate(
      data
    )}`;
    return await service.put(endpoint, data);
  },
  async deleteMailtemplate(data) {
    const endpoint = `${BASE_API_URL}${MAILTEMPLATE_API.deleteMailtemplate(
      data
    )}`;
    return await service.delete(endpoint);
  },
  async showMailtemplate(data) {
    const endpoint = `${BASE_API_URL}${MAILTEMPLATE_API.showMailtemplate(
      data
    )}`;
    return await service.get(endpoint);
  },
};

export const consentApi = {
  async getConsentDocuments(params) {
    const endpoint = `${BASE_API_URL}${CONSENT_API.consentDocuments}`;
    const config = {
      params: params,
    };

    return await service.get(endpoint, config);
  },
};

export const signatureApi = {
  async showSignatureByPaper(params) {
    const config = {
      params: params,
    };
    const endpoint = `${BASE_API_URL}${SIGNATURE_API.showSignatureByPaper}`;
    return await service.get(endpoint, config);
  },
};

/**
 * 홈페이지 컨텐츠 관련 API 요청
 */
export const homepageContentApi = {
  async getHomepageContents(params) {
    const endpoint = `${BASE_API_URL}${HOMEPAGE_CONTENT_API.homepageContent}`;
    const config = {
      params: params,
    };

    return await service.get(endpoint, config);
  },
  async createHomepageContent(data) {
    const endpoint = `${BASE_API_URL}${HOMEPAGE_CONTENT_API.createContent}`;
    return await service.post(endpoint, data);
  },
  async updateHomepageContent(data) {
    const endpoint = `${BASE_API_URL}${HOMEPAGE_CONTENT_API.updateContent(
      data
    )}`;
    return await service.put(endpoint, data);
  },
  async showHomepageContent(data) {
    const endpoint = `${BASE_API_URL}${HOMEPAGE_CONTENT_API.showContent(data)}`;
    return await service.get(endpoint);
  },
};

export const authorApi = {
  async getAuthors(params) {
    const endpoint = `${BASE_API_URL}${AUTHOR_API.author}`;
    const config = {
      params: params,
    };

    return await service.get(endpoint, config);
  },
  async createAuthor(data) {
    const endpoint = `${BASE_API_URL}${AUTHOR_API.createAuthor}`;
    return await service.post(endpoint, data);
  },
  async updateAuthor(data) {
    const endpoint = `${BASE_API_URL}${AUTHOR_API.updateAuthor(data)}`;
    return await service.put(endpoint, data);
  },
  async deleteAuthor(data) {
    const endpoint = `${BASE_API_URL}${AUTHOR_API.deleteAuthor(data)}`;
    return await service.delete(endpoint);
  },
  async getPreviousAuthors(params) {
    const endpoint = `${BASE_API_URL}${AUTHOR_API.showPreviousAuthors}`;
    const config = {
      params: params,
    };
    return await service.get(endpoint, config);
  },
};

export const fileApi = {
  async uploadFile(file) {
    const endpoint = `${BASE_API_URL}${FILE_API.uploadFile}`;
    const config = {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    };
    return await service.post(endpoint, file, config);
  },
  async deleteFile(params) {
    const config = {
      params: params,
    };
    const endpoint = `${BASE_API_URL}${FILE_API.deleteFile}`;
    return await service.delete(endpoint, config);
  },
  async fileDownload(params) {
    const endpoint = `${BASE_API_URL}${FILE_API.fileDownload}`;
    const config = {
      params: params,
      responseType: "blob", // Blob 형식으로 응답 받음
    };
    return await service.get(endpoint, config);
  },
};

export const archiveApi = {
  async showAllByCateogory(params) {
    const endpoint = `${BASE_API_URL}${ARCHIVE_API.showAllByCateogory}`;
    const config = {
      params: params,
    };
    return await service.get(endpoint, config);
  },
  async createArchive(data) {
    const endpoint = `${BASE_API_URL}${ARCHIVE_API.createArchive}`;
    return await service.post(endpoint, data);
  },

  async updateArchive(data) {
    const endpoint = `${BASE_API_URL}${ARCHIVE_API.updateArchive(data)}`;
    return await service.put(endpoint, data);
  },

  async deleteArchive(data) {
    const endpoint = `${BASE_API_URL}${ARCHIVE_API.deleteArchive(data)}`;
    return await service.delete(endpoint);
  },

  async showArchive(data) {
    const endpoint = `${BASE_API_URL}${ARCHIVE_API.showArchive(data)}`;
    return await service.get(endpoint);
  },
};

export const galleryApi = {
  async showAll(params) {
    const endpoint = `${BASE_API_URL}${GALLERY_API.showGalleries}`;
    const config = {
      params: params,
    };
    return await service.get(endpoint, config);
  },
  async createGallery(data) {
    const endpoint = `${BASE_API_URL}${GALLERY_API.createGallery}`;
    return await service.post(endpoint, data);
  },

  async updateGallery(data) {
    const endpoint = `${BASE_API_URL}${GALLERY_API.updateGallery(data)}`;
    return await service.put(endpoint, data);
  },

  async deleteGallery(data) {
    const endpoint = `${BASE_API_URL}${GALLERY_API.deleteGallery(data)}`;
    return await service.delete(endpoint);
  },

  async showGallery(data) {
    const endpoint = `${BASE_API_URL}${GALLERY_API.showGallery(data)}`;
    return await service.get(endpoint);
  },
};

export const journalApi = {
  async showAll(params) {
    const endpoint = `${BASE_API_URL}${JOURNAL_API.journals}`;
    const config = {
      params: params,
    };
    return await service.get(endpoint, config);
  },
  async showAllForSubmissions(params) {
    const endpoint = `${BASE_API_URL}${JOURNAL_API.journalsForSubmissions}`;
    const config = {
      params: params,
    };
    return await service.get(endpoint, config);
  },
  async createJournal(data) {
    const endpoint = `${BASE_API_URL}${JOURNAL_API.createJournal}`;
    return await service.post(endpoint, data);
  },
  async updateJournal(data) {
    const endpoint = `${BASE_API_URL}${JOURNAL_API.updateJournal(data)}`;
    return await service.put(endpoint, data);
  },
  async showJournal(data) {
    const endpoint = `${BASE_API_URL}${JOURNAL_API.showJournal(data)}`;
    return await service.get(endpoint);
  },
  async showJournalForAgreement(data) {
    const endpoint = `${BASE_API_URL}${JOURNAL_API.showJournalForAgreement(
      data
    )}`;
    return await service.get(endpoint);
  },
  async showJournalForPublish(params) {
    const endpoint = `${BASE_API_URL}${JOURNAL_API.showJournalForPublish}`;
    const config = {
      params: params,
    };
    return await service.get(endpoint, config);
  },
  async updateJournalBackcover(data) {
    const endpoint = `${BASE_API_URL}${JOURNAL_API.updateJournalBackcover(data)}`;
    return await service.put(endpoint, data);
  },
  async updateJournalCover(data) {
    const endpoint = `${BASE_API_URL}${JOURNAL_API.updateJournalCover(data)}`;
    return await service.put(endpoint, data);
  },
  async updateJournalFixedPages(data) {
    const endpoint = `${BASE_API_URL}${JOURNAL_API.updateJournalFixedPages(data)}`;
    return await service.put(endpoint, data);
  },
  async updateJournalInfoPage(data) {
    const endpoint = `${BASE_API_URL}${JOURNAL_API.updateJournalInfoPage(data)}`;
    return await service.put(endpoint, data);
  },
  async updateJournalTitlePage(data) {
    const endpoint = `${BASE_API_URL}${JOURNAL_API.updateJournalTitlePage(data)}`;
    return await service.put(endpoint, data);
  },
  async updateJournalInnerBack(data) {
    const endpoint = `${BASE_API_URL}${JOURNAL_API.updateJournalInnerBack(data)}`;
    return await service.put(endpoint, data);
  },
};

export const consentDocumentApi = {
  async showAll(params) {
    const endpoint = `${BASE_API_URL}${CONSENT_DOCUMENT_API.consentDocuments}`;
    const config = {
      params: params,
    };
    return await service.get(endpoint, config);
  },
  async createConsentDocument(data) {
    const endpoint = `${BASE_API_URL}${CONSENT_DOCUMENT_API.createConsentDocument}`;
    return await service.post(endpoint, data);
  },
  async updateConsentDocument(data) {
    const endpoint = `${BASE_API_URL}${CONSENT_DOCUMENT_API.updateConsentDocument(
      data
    )}`;
    return await service.put(endpoint, data);
  },
  async showConsentDocument(data) {
    const endpoint = `${BASE_API_URL}${CONSENT_DOCUMENT_API.showConsentDocument(
      data
    )}`;
    return await service.get(endpoint);
  },
};
export const consentDocumentTypeApi = {
  async showAll(params) {
    const endpoint = `${BASE_API_URL}${CONSENT_DOCUMENT_TYPE_API.consentDocumentTypes}`;
    const config = {
      params: params,
    };
    return await service.get(endpoint, config);
  },
};

export const journalTypeApi = {
  async showAll(params) {
    const endpoint = `${BASE_API_URL}${JOURNAL_TYPE_API.journalTypes}`;
    const config = {
      params: params,
    };
    return await service.get(endpoint, config);
  },
};

export const journalContentApi = {
  async showAll(params) {
    const endpoint = `${BASE_API_URL}${JOURNAL_CONTENT_API.journalContents}`;
    const config = {
      params: params,
    };
    return await service.get(endpoint, config);
  },
  async createJournalContent(data) {
    const endpoint = `${BASE_API_URL}${JOURNAL_CONTENT_API.createJournalContent}`;
    return await service.post(endpoint, data);
  },
  async updateJournalContent(data) {
    const endpoint = `${BASE_API_URL}${JOURNAL_CONTENT_API.updateJournalContent(
      data
    )}`;
    return await service.put(endpoint, data);
  },
  async updateJournalContentGroup(data) {
    const endpoint = `${BASE_API_URL}${JOURNAL_CONTENT_API.updateJournalContentGroup(
      data
    )}`;
    return await service.patch(endpoint, data);
  },
  async updateJournalContentAcceptedStatus(data) {
    const endpoint = `${BASE_API_URL}${JOURNAL_CONTENT_API.updateJournalContentAcceptedStatus(
      data
    )}`;
    return await service.put(endpoint, data);
  },
  async showJournalContent(data) {
    const endpoint = `${BASE_API_URL}${JOURNAL_CONTENT_API.showJournalContent(
      data
    )}`;
    return await service.get(endpoint);
  },
  async showAllByCategory(params) {
    const endpoint = `${BASE_API_URL}${JOURNAL_CONTENT_API.showJournalContentsByCategory}`;
    const config = {
      params: params,
    };
    return await service.get(endpoint, config);
  },
  async showAllNonGroup(params) {
    const endpoint = `${BASE_API_URL}${JOURNAL_CONTENT_API.showAllNonGroupJournalContents}`;
    const config = {
      params: params,
    };
    return await service.get(endpoint, config);
  },
  async showContentsByGroup(params) {
    const endpoint = `${BASE_API_URL}${JOURNAL_CONTENT_API.showContentsByGroup}`;
    const config = {
      params: params,
    };
    return await service.get(endpoint, config);
  },
  async updateJournalContentOrder(data) {
    const endpoint = `${BASE_API_URL}${JOURNAL_CONTENT_API.updateJournalContentOrder}`;
    return await service.patch(endpoint, data);
  },
  async deleteJournalContent(data) {
    const endpoint = `${BASE_API_URL}${JOURNAL_CONTENT_API.deleteJournalContent(
      data
    )}`;
    return await service.delete(endpoint);
  },
  async finalPublish(params, body) {
    const endpoint = `${BASE_API_URL}${JOURNAL_CONTENT_API.finalPublish}`;
    const config = {
      params: params,
    };
    return await service.patch(endpoint, body, config);
  },

  /**
   * 간행물에 대한 컨텐츠 리스트
   *
   * @param {Object} pathParams - 경로 파라미터 {journal_idx: 간행물 인덱스}
   * @param {Object} queryParams - 쿼리 파라미터 {category: 카테고리(선택), is_published: 저널 게시 체크 여부(선택)}
   * @returns {Promise<Object>} - 컨텐츠 리스트
   */
  async showJournalContentByJournalAndCategory({ pathParams, queryParams }) {
    const endpoint = `${BASE_API_URL}${JOURNAL_CONTENT_API.showJournalContentByJournalAndCategory(
      pathParams
    )}`;
    const config = {
      params: queryParams,
    };
    return await service.get(endpoint, config);
  },
};

export const journalContentGroupApi = {
  async showAll(params) {
    const endpoint = `${BASE_API_URL}${JOURNAL_CONTENT_GROUP_API.journalContentGroups}`;
    const config = {
      params: params,
    };
    return await service.get(endpoint, config);
  },
  async createJournalContentGroup(data) {
    const endpoint = `${BASE_API_URL}${JOURNAL_CONTENT_GROUP_API.createJournalContentGroup}`;
    return await service.post(endpoint, data);
  },
  async updateJournalContentGroup(data) {
    const endpoint = `${BASE_API_URL}${JOURNAL_CONTENT_GROUP_API.updateJournalContentGroup(
      data
    )}`;
    return await service.put(endpoint, data);
  },
  async deleteJournalContentGroup(data) {
    const endpoint = `${BASE_API_URL}${JOURNAL_CONTENT_GROUP_API.deleteJournalContentGroup(
      data
    )}`;
    return await service.delete(endpoint);
  },
  async showJournalContentGroup(data) {
    const endpoint = `${BASE_API_URL}${JOURNAL_CONTENT_GROUP_API.showJournalContentGroup(
      data
    )}`;
    return await service.get(endpoint);
  },
  async updateJournalContentGroupOrder(data) {
    const endpoint = `${BASE_API_URL}${JOURNAL_CONTENT_GROUP_API.updateJournalContentGroupOrder}`;
    return await service.patch(endpoint, data);
  },
};

export const paperCommentApi = {
  async createPaperComment(data) {
    const endpoint = `${BASE_API_URL}${PAPER_COMMENT_API.createPaperComment}`;
    return await service.post(endpoint, data);
  },
  async updatePaperComment(data) {
    const endpoint = `${BASE_API_URL}${PAPER_COMMENT_API.updatePaperComment(
      data
    )}`;
    return await service.put(endpoint, data);
  },
  async deletePaperComment(data) {
    const endpoint = `${BASE_API_URL}${PAPER_COMMENT_API.deletePaperComment(
      data
    )}`;
    return await service.delete(endpoint);
  },
  async getPaperComments(params) {
    const endpoint = `${BASE_API_URL}${PAPER_COMMENT_API.getPaperComments}`;
    const config = {
      params: params,
    };
    return await service.get(endpoint, config);
  },
};
